import { useEffect, useState } from "react";
import StatCard from "pages/dashboard-overview/components/StatCard";
import StatCardSkeleton from "pages/analytics/components/StatCardSkeleton";
import { ErrorBanner } from "pages/analytics/components/AnalyticsStates";
import { formatCurrency, formatNumber, formatPercent } from "pages/analytics/components/analyticsFormatters";
import { getFinanceKpis } from "services/analytics-truth.service";
import { getKpis, getUpgradeMetrics } from "services/finance.service";
import { isCustomRangeIncomplete } from "utils/analyticsQueryParams";

const isUpgradeMetricsUnavailable = (filters) =>
  filters?.dateRangeType === "allTime";

export default function FinanceKpiCards({ api, filters }) {
  const [kpis, setKpis] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setKpis(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [financeKpis, reportKpis, upgradeMetrics] = await Promise.all([
          getFinanceKpis(api, { filters }),
          getKpis(api, { filters }),
          isUpgradeMetricsUnavailable(filters)
            ? Promise.resolve(null)
            : getUpgradeMetrics(api, { filters }),
        ]);
        if (cancelled) return;

        setKpis({
          salesCount: financeKpis?.salesCount ?? 0,
          revenue: financeKpis?.revenue ?? 0,
          mrr: financeKpis?.mrr ?? 0,
          netProfitMargin: reportKpis?.profitMargin ?? 0,
          blueprintUpgradeCount: isUpgradeMetricsUnavailable(filters)
            ? null
            : (upgradeMetrics?.blueprintUpgradeCount ?? 0),
          blueprintUpgradeRate: isUpgradeMetricsUnavailable(filters)
            ? null
            : upgradeMetrics?.blueprintUpgradeRate ?? null,
          icUpsellConversionRate: isUpgradeMetricsUnavailable(filters)
            ? null
            : upgradeMetrics?.icUpsellConversionRate ?? null,
        });
      } catch {
        if (!cancelled) {
          setKpis(null);
          setError("Failed to load finance KPIs.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [api, filters.dateRangeType, filters.customDateRange?.[0], filters.customDateRange?.[1]]);

  if (isCustomRangeIncomplete(filters) && !isLoading) {
    return (
      <p className="text-sm text-gray-500 italic mb-6">
        Select start and end dates to view KPIs.
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {[...Array(6)].map((_, i) => (
          <StatCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-6">
        <ErrorBanner message={error} />
      </div>
    );
  }

  return (
    <div className="mb-6">
      <h2 className="text-lg font-medium text-gray-900 mb-3">KPIs</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard title="Revenue" value={formatCurrency(kpis?.revenue)} icon="DollarSign" />
        <StatCard title="Sales Count" value={formatNumber(kpis?.salesCount)} icon="ShoppingCart" />
        <StatCard title="MRR" value={formatCurrency(kpis?.mrr)} icon="Repeat" />
        <StatCard
          title="Net Profit Margin %"
          value={formatPercent(kpis?.netProfitMargin)}
          icon="PieChart"
        />
        {/* <StatCard
          title="Blueprint Upgrade Count"
          value={
            kpis?.blueprintUpgradeCount == null
              ? "-"
              : formatNumber(kpis?.blueprintUpgradeCount)
          }
          icon="ArrowUpRight"
        /> */}
        <StatCard
          title="Blueprint Upgrade Rate %"
          value={formatPercent(kpis?.blueprintUpgradeRate)}
          icon="TrendingUp"
        />
        <StatCard
          title="IC Upsell Rate %"
          value={formatPercent(kpis?.icUpsellConversionRate)}
          icon="TrendingUp"
        />
      </div>
    </div>
  );
}
